import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { BrandLogo } from "./common";
import { toast } from "sonner";

/**
 * LogoUploader — drag/drop or click to upload an image (PNG/JPEG/WEBP/SVG).
 * Displays current logo if value is set. Clears with X button.
 */
export default function LogoUploader({ value, onChange, name = "Logo", size = 56, aspect = "square", testid = "logo-uploader" }) {
  const [busy, setBusy] = useState(false);
  const [hovering, setHovering] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    // Hard cap at 256 KB so the resulting data URI fits comfortably in a MongoDB doc and
    // survives every redeploy. Brand/partner logos rarely need to be larger than this.
    if (file.size > 256 * 1024) {
      toast.error(`Logo is ${Math.round(file.size/1024)} KB — max 256 KB. Compress or resize first (we recommend 200×80 px for wordmarks, 200×200 for squares).`);
      return;
    }
    setBusy(true);
    try {
      // Read the file as a base64 data URI and store it directly so the logo persists
      // forever — no external storage dependency, no per-deploy re-upload.
      const dataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsDataURL(file);
      });
      onChange(dataUri);
      toast.success(`Logo saved (${Math.round(file.size/1024)} KB) — persists across deploys`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setHovering(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  const isHorizontal = aspect === "horizontal";
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {isHorizontal ? (
        <div className="relative flex items-center justify-center bg-white border border-[var(--border-default)] rounded-lg p-2" style={{ height: size, minWidth: size * 2, maxWidth: size * 4 }}>
          {value
            ? <img src={value} alt={name} className="h-full w-auto max-w-full object-contain" />
            : <span className="text-xs text-[var(--text-secondary)]">No logo</span>}
          {value && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-[var(--border-default)] rounded-full flex items-center justify-center hover:bg-[var(--brand-tint)] hover:border-[var(--brand)] transition-colors" title="Remove logo" data-testid={`${testid}-clear`}>
              <X size={11} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative" style={{ width: size, height: size }}>
          <BrandLogo name={name} logoUrl={value} size={size} />
          {value && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-[var(--border-default)] rounded-full flex items-center justify-center hover:bg-[var(--brand-tint)] hover:border-[var(--brand)] transition-colors" title="Remove logo" data-testid={`${testid}-clear`}>
              <X size={11} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      )}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setHovering(true); }}
        onDragLeave={() => setHovering(false)}
        onDrop={onDrop}
        className={`flex-1 p-3 rounded-lg border border-dashed text-xs cursor-pointer transition-colors ${hovering ? "border-[var(--brand)] bg-[var(--brand-tint)]" : "border-[var(--border-dashed)] hover:border-[var(--brand)] hover:bg-[var(--brand-tint)]/30"}`}>
        <div className="flex items-center gap-2 text-[var(--text-secondary)]">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          <span>{busy ? "Uploading…" : value ? "Replace logo" : "Upload logo or drag & drop"}</span>
        </div>
        <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">PNG, JPEG, WEBP or SVG · max 2MB{isHorizontal ? " · horizontal wordmarks work best" : ""}</div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
    </div>
  );
}
